"use client";

import React, {useEffect, useState, ReactNode} from "react";
import styles from "./SDialog.module.css";
import FontIcon from "@/app/_components/FontIcon/fontIcon";
import {DialogContext, DialogContextType} from "@/Master/SDialogFC/SDialogContext";
import {useDialogMethods} from "@/Master/SDialogFC/useDialogMethods";

/** 函数式 SDialogFC 组件的 Props */
export interface SDialogProps<T = {}> {
    fcKey: string;
    /** 是否展示弹窗 */
    isOpen?: boolean;

    /** 模式：Center 或 Full */
    mode?: "Center" | "Full";

    /** 标题、图标、底部按钮、额外渲染等 */
    title?: ReactNode;
    icon?: ReactNode;
    buttons?: { label: string; onClick: () => void; style?: string }[];
    bottom?: ReactNode;

    /** 主体内容的渲染函数，或直接传入 children 也可 */
    children?: React.ReactNode; // 弹窗内容
    /** 关闭回调 */
    onClose?: (s?: any) => void;

    // 全屏下的关闭按钮是否显示
    isFullCloseBtn?: boolean;

    //是否显示关闭动画
    isCloseHideAni?: boolean;

}

/**
 * 函数式的基础 SDialogFC
 */
export const SDialogFC: React.FC<SDialogProps> = <T extends {}>(config: SDialogProps<T>) => {
    const {
        fcKey,
        mode,
        title,
        icon,
        buttons,
        bottom,
        children,
        onClose,
        isFullCloseBtn = false,
        isCloseHideAni,
    } = config;

    const [isRunClose, setIsRunClose] = useState(false)
    const [isOpen, setIsOpen] = useState<boolean>(true);
    const {openDialog, closeDialog } = useDialogMethods(fcKey,{
        open:()=>{
            openDialog();
        },
        close:()=>{
            closeDialog();
        }
    });

    useEffect(() => {
        // 当弹窗打开时，禁用背景滚动
        if (isOpen) {
            // 保存当前的 overflow 样式
            const originalStyle = window.getComputedStyle(document.body).overflow;
            // 禁用滚动
            document.body.style.overflow = 'hidden';
            // 处理 iOS 设备上的触摸事件
            const handleTouchMove = (e: TouchEvent) => {
                e.preventDefault();
            };
            document.body.addEventListener('touchmove', handleTouchMove, { passive: false });
            // 清理函数，恢复原样
            return () => {
                document.body.style.overflow = originalStyle;
                document.body.removeEventListener('touchmove', handleTouchMove);
            };
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isCloseHideAni){
            if (!isOpen && !isRunClose){
                setIsRunClose(true);
                //等待关闭动画完成
                setTimeout(() => {
                    onClose?.(); // 通知父级
                    closeDialog();
                }, 300);
            }
        }
        return () => {
            // console.error('卸载',this)
        };
    }, [isOpen,isRunClose]);


    //点击关闭按钮
    const onClickHide = () => {
        if (!isOpen) return;
        setIsOpen(false);
        if (isCloseHideAni){
            onClose?.(); // 通知父级
            closeDialog();
        }
    };

    // 根据 mode 不同渲染
    if (mode === "Full") {
        // Full 模式
        return (
            <div className={`${styles.overlay} ${styles.show}`} onClick={onClickHide}>
                <div
                    className={`${styles.full} ${isOpen ? styles.showDialog : styles.hideDialog}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* 可以按需加关闭按钮 */}
                    {isFullCloseBtn && <button className={`btn ${styles.closeButton}`} onClick={onClickHide}>
                        <FontIcon icon={`s-close ${styles.closeIcon}`}/>
                    </button>}
                    {children}
                </div>
            </div>
        );
    } else {
        return (
            <div
                className={`${styles.overlay} ${styles.show}`}
                onClick={onClickHide}
                onTouchMove={(e) => e.stopPropagation()}
            >
                <div
                    className={`${styles.container} ${isOpen ? styles.showDialog : styles.hideDialog}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        className={`btn ${styles.closeButton}`}
                        onClick={onClickHide}
                    >
                        <FontIcon icon={`s-close ${styles.closeIcon}`}/>
                    </button>

                    <div className={`${styles.header} center`}>
                        {icon && <div className={`${styles.icon} center`}>{icon}</div>}
                        {title && <div className={styles.title}>{title}</div>}
                    </div>

                    <div className={styles.body}>
                        <div className={styles.bodyCenter}>
                            {children}
                        </div>
                    </div>

                    <div className={styles.footer}>
                        {bottom}
                        {buttons?.map((btn, i) => (
                            <button
                                key={i}
                                className={`btn ${styles.defaultBtn} ${
                                    styles[btn.style || "defaultBtn"]
                                }`}
                                onClick={btn.onClick}
                            >
                                {btn.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }
};
